import { useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Avatar, Box, Button, CircularProgress, Popover, Skeleton, TextField, Typography } from '@mui/material';
import { useEngagementManager } from 'dashboard409a/hooks/useEngagementManager';
import { getInitials } from 'utilities';
import { EngagementManagerCellProps } from './cells.props';

const EngagementManagerCell = ({ valuationRow, onEngagementManagerUpdate }: EngagementManagerCellProps) => {
  const cellRef = useRef<HTMLDivElement>(null);
  
  const {
    anchorEl,
    searchTerm,
    searching,
    engagementManagers,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleSelectEngagementManager,
    handleSearch,
    handleKeyDown,
    setSearchTerm,
  } = useEngagementManager({ valuationRow, onEngagementManagerUpdate });

  if (valuationRow.is_loading_additional_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }

  const cellContent =
    valuationRow.engagement_manager && valuationRow.engagement_manager !== '-' ? (
      <Box
        ref={cellRef}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}>
        <Avatar
          alt={valuationRow.engagement_manager}
          src={
            valuationRow.engagement_manager_image_src && valuationRow.engagement_manager_image_src !== '-'
              ? valuationRow.engagement_manager_image_src
              : undefined
          }
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: valuationRow.engagement_manager })}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {valuationRow.engagement_manager}
          </Typography>
        </Box>
      </Box>
    ) : (
      <Box
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}>
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      </Box>
    );

  return (
    <>
      {cellContent}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        <Box sx={{ p: 2, minWidth: 300 }}>
          <TextField
            fullWidth
            placeholder="Search for engagement manager..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            disabled={!searchTerm.trim() || searching}>
            Search
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>You can press Enter to search.</Typography>
          {searching ? (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : engagementManagers.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {engagementManagers.map(engagementManager => (
                <Box key={engagementManager.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => handleSelectEngagementManager(engagementManager.targetRecordId)}>
                  <Avatar
                    alt={engagementManager.name}
                    src={engagementManager.imageSrc}
                    sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                    {getInitials({ name: engagementManager.name })}
                  </Avatar>
                  <Typography variant="body2">{engagementManager.name}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default EngagementManagerCell;

